import { useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { AuthContext, AuthStatus } from "@/contexts/AuthContext";
import { getUrlWithQuery } from "@/libs/url";
import {
  MeQuery,
  useGetCompanyInfoLazyQuery,
  useMeLazyQuery,
} from "@/generated/graphql";
import { ErrorCode, moveToErrorPage } from "@/libs/errors";
import { BILLING_RENEWAL_DATE } from "@/libs/constants";
import config from "@/libs/config";

type Props = {
  authStatus: AuthStatus;
  redirectToIfSignedIn?: string;
  redirectToIfSignedInParams?: { [name: string]: string | string[] };
  redirectToIfSignedOut?: string;
  redirectToIfSignedOutParams?: { [name: string]: string | string[] };
  isNotReady?: boolean; // authStatus 以外で実行を制御したい場合に指定する
  skipCompanyInfoCheck?: boolean; // 事業者情報登録チェックが不要な場合はtrueを指定する
  skipResignTermsOfServiceCheck?: boolean; // 規約再同意チェックが不要な場合はtrueを指定する
  skipEmailVerifiedCheck?: boolean; // メールアドレス認証チェックが不要な場合はtrueを指定する
};

// 認証状態に合わせてページを遷移する。
//
// ケースA (/sign-in など):
// - サインイン状態の場合指定のページへ遷移する
// - サインアウト状態の場合children を指定する
// -> redirectToIfSignedIn を指定する
//
// ケースB (/select-service など):
// - サインイン状態の場合children を描画する
// - サインアウト状態の場合 /sign-in へ遷移する
// -> redirectToIfSignedOut に/sign-in を指定する
//
// ケースC (index など):
// - サインイン状態の場合指定のページへ遷移する
// - サインアウト状態の場合指定のページへ遷移する
// -> 両方を指定する
//
// なお、どのケースでも
// - newPasswordRequired の場合 /sign-in に遷移する
// - isNotReady にfalse が指定されている場合遷移しない
//
// また、サインイン状態の場合、事業者情報登録チェックを行う
// 事業者情報が登録されていない場合は事業者情報登録画面に遷移する
const useCheckAuth = ({
  authStatus,
  redirectToIfSignedIn,
  redirectToIfSignedInParams,
  redirectToIfSignedOut,
  redirectToIfSignedOutParams,
  isNotReady,
  skipCompanyInfoCheck,
  skipResignTermsOfServiceCheck,
  skipEmailVerifiedCheck,
}: Props) => {
  const {
    newPasswordRequired,
    isCompanyInfoRegistered,
    setCompanyInfoRegistered,
    isResignedTermsOfService,
    setResignedTermsOfService,
    userInfo,
  } = useContext(AuthContext);
  const router = useRouter();
  const [passed, setPassed] = useState(false);
  const { app } = router.query;
  const isAdmin = userInfo.authority === "admin";

  const [getCompanyInfoQuery] = useGetCompanyInfoLazyQuery({
    onError: (err) => {
      moveToErrorPage(ErrorCode.INTERNAL_SERVER_ERROR);
    },
  });

  const [meQuery] = useMeLazyQuery({
    onError: (err) => {
      moveToErrorPage(ErrorCode.INTERNAL_SERVER_ERROR);
    },
  });

  const isUserCreatedAfterBillingRenewal = (meQuery: MeQuery): boolean => {
    if (!meQuery?.me?.createdAt) {
      return false;
    }
    const createdAt = new Date(meQuery.me.createdAt);
    return BILLING_RENEWAL_DATE <= createdAt;
  };

  useEffect(() => {
    if (authStatus === AuthStatus.Loading || isNotReady || !router.isReady) {
      return;
    }

    if (newPasswordRequired) {
      router.push(getUrlWithQuery("/sign-in", app));
      return;
    }

    if (authStatus === AuthStatus.SignedOut && redirectToIfSignedOut) {
      router.push(
        getUrlWithQuery(redirectToIfSignedOut, app, redirectToIfSignedOutParams)
      );
      return;
    }

    if (authStatus === AuthStatus.SignedIn && redirectToIfSignedIn) {
      router.push(
        getUrlWithQuery(redirectToIfSignedIn, app, redirectToIfSignedInParams)
      );
      return;
    }

    const requiredCompanyInfoCheck =
      authStatus === AuthStatus.SignedIn &&
      !isCompanyInfoRegistered &&
      app !== "loan" &&
      !skipCompanyInfoCheck;

    const requiredResignTermsOfServiceCheck =
      authStatus === AuthStatus.SignedIn &&
      isAdmin &&
      !isResignedTermsOfService &&
      !skipResignTermsOfServiceCheck;

    const requiredEmailVerified =
      isAdmin &&
      !userInfo.emailVerified &&
      !config.IS_SHIKOKUBANK && // 暫定対応: 四国銀行は制御対象から除外／常にメアド未認証で運用するため
      !skipEmailVerifiedCheck;

    if (
      !requiredCompanyInfoCheck &&
      !requiredResignTermsOfServiceCheck &&
      !requiredEmailVerified
    ) {
      setPassed(true);
      return;
    }

    (async () => {
      if (requiredCompanyInfoCheck) {
        const result = await getCompanyInfoQuery();
        if (result.data?.company) {
          setCompanyInfoRegistered();
        } else {
          if (isAdmin) {
            router.push(getUrlWithQuery("/register-company-info", app));
          } else {
            moveToErrorPage(ErrorCode.NO_REGISTRATION_COMPANY_INFO);
          }
          return;
        }
      }

      if (requiredResignTermsOfServiceCheck) {
        const result = await meQuery();
        if (
          result.data?.me?.resignedTermsOfService ||
          isUserCreatedAfterBillingRenewal(result.data)
        ) {
          setResignedTermsOfService();
        } else {
          router.push(getUrlWithQuery("/confirm-renewal-information", app));
          return;
        }
      }

      if (requiredEmailVerified) {
        router.push(
          getUrlWithQuery(`/request-verify-email-address/${userInfo.sub}`, app)
        );
        return;
      }
      setPassed(true);
    })();
  }, [authStatus, isNotReady, router.isReady]);

  return { passed };
};

export default useCheckAuth;
